import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { CollapsibleIntegration } from "./CollapsibleIntegration";

/**
 * VK (VKontakte) community DM integration block.
 *
 * Lets a salon owner connect their VK community so the existing AI chat
 * assistant answers visitors in VK group messages and forwards parsed
 * bookings through the same email/Telegram/IDENT pipeline.
 *
 * UX flow (4 steps, all on the user's side):
 *   1. Copy the webhook URL (auto-generated, salon-specific)
 *   2. Paste it into VK → Community → Manage → API usage → Callback API
 *   3. VK shows a confirmation code → user pastes it into Chatity
 *   4. Paste the Community Access Token + Group ID → Save
 */
export const VkIntegrationBlock = ({ API }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Form state
  const [groupToken, setGroupToken] = useState("");
  const [groupId, setGroupId] = useState("");
  const [confirmationCode, setConfirmationCode] = useState("");

  const fetchStatus = async () => {
    try {
      const res = await axios.get(`${API}/integrations/vk/status`);
      setStatus(res.data);
      // Pre-fill form when already connected
      if (res.data?.enabled) {
        setGroupId(res.data.group_id || "");
        setConfirmationCode(res.data.confirmation_code || "");
      }
    } catch {
      setStatus(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStatus();
  }, []);

  const handleConnect = async () => {
    if (!groupId.trim() || !confirmationCode.trim()) {
      toast.error("Заполните ID сообщества и код подтверждения");
      return;
    }
    setBusy(true);
    try {
      const res = await axios.post(`${API}/integrations/vk/connect`, {
        group_token: groupToken.trim() || null,
        group_id: groupId.trim(),
        confirmation_code: confirmationCode.trim(),
      });
      if (res.data?.token_ok) {
        toast.success("VK подключён, AI начнёт отвечать в сообществе");
      } else if (res.data?.token_error) {
        toast.error(`Код сохранён, но токен не принят: ${res.data.token_error}`);
      } else {
        toast.success(
          "Код сохранён. Теперь нажмите «Подтвердить» в VK, затем вернитесь и введите ключ доступа."
        );
      }
      setGroupToken("");
      await fetchStatus();
      if (res.data?.webhook_url) {
        setStatus((s) => ({ ...(s || {}), webhook_url: res.data.webhook_url }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Не удалось сохранить");
    } finally {
      setBusy(false);
    }
  };

  const handleDisconnect = async () => {
    if (!window.confirm("Отключить VK? AI перестанет отвечать в личке сообщества.")) return;
    setBusy(true);
    try {
      await axios.post(`${API}/integrations/vk/disconnect`);
      toast.success("VK-интеграция отключена");
      setGroupToken("");
      setGroupId("");
      setConfirmationCode("");
      await fetchStatus();
    } catch (e) {
      toast.error("Не удалось отключить");
    } finally {
      setBusy(false);
    }
  };

  const handleTest = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/integrations/vk/test`);
      if (res.data?.ok) {
        toast.success(`Подключение работает: ${res.data.group_name || "OK"}`);
      } else {
        toast.error(`VK ответил: ${res.data?.error || "неизвестная ошибка"}`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Тест не прошёл");
    } finally {
      setBusy(false);
    }
  };

  const copy = (txt) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt).then(
      () => toast.success("Скопировано"),
      () => toast.error("Не удалось скопировать"),
    );
  };

  if (loading) {
    return (
      <div className="bg-white border-2 border-gray-300 rounded-lg p-4 text-sm text-gray-500">
        Загрузка VK-интеграции…
      </div>
    );
  }

  const enabled = !!status?.enabled;
  const webhookUrl = status?.webhook_url || "";

  return (
    <CollapsibleIntegration
      testId="vk-integration-block"
      logo={
        <span
          className="inline-flex items-center justify-center w-full h-full rounded-md text-white font-bold text-lg"
          style={{ background: "#0077FF" }}
        >
          VK
        </span>
      }
      title="ВКонтакте"
      enabled={enabled}
      description="AI отвечает в личке сообщества и собирает заявки в email/Telegram"
    >
      <div className="space-y-4 pt-3">

      {enabled && (
        <div className="grid grid-cols-2 gap-4 bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
          <div>
            <div className="text-emerald-700 font-medium">ID сообщества</div>
            <div className="text-gray-800 font-mono">{status?.group_id || "—"}</div>
          </div>
          <div>
            <div className="text-emerald-700 font-medium">За последние 24ч</div>
            <div className="text-gray-800">
              {status?.total_messages_24h || 0} сообщений
            </div>
          </div>
          {status?.last_message_at && (
            <div className="col-span-2">
              <div className="text-emerald-700 font-medium">Последняя активность</div>
              <div className="text-gray-800 font-mono text-xs">
                {new Date(status.last_message_at).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Step 1: webhook URL — always visible */}
      <div className="space-y-2">
        <Label className="font-semibold">Шаг 1. URL вебхука</Label>
        <div className="flex gap-2">
          <Input
            value={webhookUrl}
            readOnly
            className="font-mono text-xs"
            data-testid="vk-webhook-url"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => copy(webhookUrl)}
            data-testid="vk-copy-webhook"
          >
            Копировать
          </Button>
        </div>
        <p className="text-xs text-gray-500">
          В VK: <b>Сообщество → Управление → Работа с API → Callback API</b> →
          вставьте URL.
        </p>
      </div>

      {/* Step 2: confirmation code */}
      <div className="space-y-2">
        <Label className="font-semibold">Шаг 2. Код подтверждения из VK</Label>
        <Input
          value={confirmationCode}
          onChange={(e) => setConfirmationCode(e.target.value)}
          placeholder="например a1b2c3d4"
          className="font-mono"
          data-testid="vk-confirmation-input"
        />
        <p className="text-xs text-gray-500">
          VK покажет код в окне Callback API. Сохраните его здесь <b>перед</b>
          нажатием «Подтвердить» в VK.
        </p>
      </div>

      {/* Step 3: group token */}
      <div className="space-y-2">
        <Label className="font-semibold">Шаг 3. Ключ доступа сообщества</Label>
        <Input
          type="password"
          value={groupToken}
          onChange={(e) => setGroupToken(e.target.value)}
          placeholder={enabled ? "•••• сохранён (введите новый чтобы заменить)" : "vk1.a.…"}
          className="font-mono"
          data-testid="vk-token-input"
        />
        <p className="text-xs text-gray-500">
          VK: <b>Управление → Работа с API → Ключи доступа → Создать ключ</b>.
          Права: «Сообщения сообщества».
        </p>
      </div>

      {/* Step 4: group id */}
      <div className="space-y-2">
        <Label className="font-semibold">Шаг 4. ID сообщества (без минуса)</Label>
        <Input
          value={groupId}
          onChange={(e) => setGroupId(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="123456789"
          className="font-mono"
          data-testid="vk-group-id-input"
        />
        <p className="text-xs text-gray-500">
          Найти можно в URL сообщества: <code>vk.com/club<b>123456789</b></code>.
          Или в Управление → Основная информация.
        </p>
      </div>

      <div className="flex gap-2 flex-wrap pt-2">
        <Button
          type="button"
          onClick={handleConnect}
          disabled={busy}
          className="bg-[#0077FF] hover:bg-[#0066DD] text-white"
          data-testid="vk-connect-btn"
        >
          {busy ? "Сохраняю…" : groupToken ? "Сохранить и подключить" : "Сохранить код"}
        </Button>
        {enabled && (
          <>
            <Button
              type="button"
              variant="outline"
              onClick={handleTest}
              disabled={busy}
              data-testid="vk-test-btn"
            >
              Проверить
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={handleDisconnect}
              disabled={busy}
              className="text-red-600 border-red-300 hover:bg-red-50"
              data-testid="vk-disconnect-btn"
            >
              Отключить
            </Button>
          </>
        )}
      </div>
      </div>
    </CollapsibleIntegration>
  );
};

export default VkIntegrationBlock;
